import { useAppSummaries, useApps } from "@features/apps/useApps.js";
import { Path } from "@util/paths";
import { useCallback } from "react";
import { useRouteMatch } from "react-router-dom";

export const APP_ROUTE_PATH = `${Path.Apps}/:appId(\\w+)`;

interface UseScopedAppProps {
  enabled: boolean;
}

const useScopedAppId = () => {
  const routeMatch = useRouteMatch<{ appId: string }>(APP_ROUTE_PATH);

  let appId = routeMatch?.params.appId;
  if (appId === "new") {
    appId = undefined;
  }

  return appId;
};

export const useScopedApp = (props?: UseScopedAppProps) => {
  const enabled = props?.enabled ?? true;
  const appId = useScopedAppId();

  const apps = useApps({
    enabled: enabled && !!appId,
    select: (apps) => apps.find((app) => app.sid === appId),
  });

  return appId ? apps : null;
};

export const useScopedAppSummary = (props?: UseScopedAppProps) => {
  const enabled = props?.enabled ?? true;
  const appId = useScopedAppId();

  const apps = useAppSummaries({
    enabled: enabled && !!appId,
    select: (apps) => apps.find((app) => app.sid === appId),
  });

  return appId ? apps : null;
};

export const buildAppScopedPath = (
  appSid: string | undefined,
  path: string,
) => {
  return appSid ? `${Path.Apps}/${appSid}${path}` : path;
};

interface UseBuildAppScopedPathOptions {
  fallbackAppSid?: string;
}

export const useBuildAppScopedPath = ({
  fallbackAppSid,
}: UseBuildAppScopedPathOptions = {}) => {
  const scopedApp = useScopedApp();
  const appSid = scopedApp?.data?.sid ?? fallbackAppSid;

  const buildPath = useCallback(
    (path: string) => buildAppScopedPath(appSid, path),
    [appSid],
  );

  return buildPath;
};
